// Stale or not, Success will *always* have data

/**
 * Produce a loading state. Forwards the current `data` if present and sets the stale flag accordingly.
 *
 * @example
 * ```ts
 *
 * const data = {
 *   data: 'foo',
 *   error: null,
 *   loading: false,
 *   stale: false
 * };
 *
 * toLoading()(data) = {
 *   data: 'foo', // Copies data
 *   error: null,
 *   loading: true, // Sets loading
 *   stale: true // Sets stale: true
 * }
 *
 * // Curried for use in conjunction with `withFunctionalNext` to automatically derive the correct state:
 *
 * runtime.next(toLoading())
 * ```
 */
export const toLoading = () => state => state !== null && state !== void 0 && state.data ? {
  data: state.data,
  error: null,
  loading: true,
  stale: true
} : {
  data: undefined,
  error: null,
  loading: true,
  stale: false
};

/**
 * Produce a success state with the given data.
 *
 * @example
 * ```ts
 *
 * const data = { id: '1' };
 *
 * toSuccess(data)() = {
 *   data: { id: '1' },
 *   error: null,
 *   loading: false,
 *   stale: false
 * }
 *
 * // Curried for use in conjunction with `withFunctionalNext` to automatically derive the correct state:
 *
 * runtime.next(toSuccess(data))
 * ```
 */
export const toSuccess = nextData => () => ({
  data: nextData,
  error: null,
  loading: false,
  stale: false
});

/**
 * Produce an error state. Forwards the current `data` if present and sets the stale flag accordingly.
 *
 * @example
 * ```ts
 *
 * const state = {
 *   data: { id: '1' },
 *   error: null,
 *   loading: false,
 *   stale: false
 * };
 *
 * toError(new Error('Failed to fetch'))(state) = {
 *   data: { id: '1' },
 *   error: Error('Failed to fetch'),
 *   loading: false,
 *   stale: true
 * }
 *
 * // Curried for use in conjunction with `withFunctionalNext` to automatically derive the correct state:
 *
 * runtime.next(toError(new Error('Failed to fetch')))
 * ```
 */
export const toError = error => state => !(state !== null && state !== void 0 && state.data) ? {
  data: undefined,
  error,
  loading: false,
  stale: false
} : {
  data: state.data,
  error,
  loading: false,
  stale: true
};

/**
 * Mark a state as stale if data is present.
 *
 * @example
 * ```ts
 *
 * const state = {
 *   data: { id: '1' },
 *   error: null,
 *   loading: false,
 *   stale: false
 * };
 *
 * markStale()(state) = {
 *   data: { id: '1' },
 *   error: null,
 *   loading: false,
 *   stale: true
 * }
 *
 * const noDataState = {
 *   error: null,
 *   loading: true,
 *   stale: false
 * };
 *
 * markStale()(noDataState) = {
 *   error: null,
 *   loading: true,
 *   stale: false
 * };
 *
 * // Curried for use in conjunction with `withFunctionalNext` to automatically derive the correct state:
 *
 * runtime.next(markStale())
 * ```
 */
export const markStale = () => state => !state.data ? Object.assign({}, state) : Object.assign({}, state, {
  data: state.data,
  stale: true
});